import './styles.css';

import React, { useCallback, useMemo } from 'react';
import { Header as LayoutComponentHeader } from '@vtblife/layout-components';
import { useShellContext } from '@vtblife/layout-components';
import { HeaderPersonalButton } from '@vtblife/layout-components/dist/types/header.interface';
import { LoginPath, LoginRole } from '@vtblife/layout-config/dist/types';

import { OfferRoute, SearchMapRoute, SearchRoute, UnsubscribeRoute, useRouter } from '@search/offer-search-routes/src';
import { errorCatcherWrap } from '@search/error/src/catcher';
import { useM2AuthContext } from '@search/auth/src/M2AuthProvider';
import classname from '@search/classname/src';
import { getDeveloperPromoName } from '@search/vtbeco-frontend-core/domain/newbuilding/developer-promo/helpers';
import { dataLayerPush } from '@search/vtbeco-frontend-core/domain/google/dataLayerPush';
import { useGeoRootRegion } from '@search/geo/src/GeoRootRegionProvider';
import { RegionIdEnum } from '@search/vtbeco-frontend-core/enums';
import { AdditionalRegionWithMetroIdEnum, AdditionalRegionWithMetroIdEnumObl } from '@search/filter-enums/enums/Region';
import { getDeveloperBannerGtmData } from '@search/vtbeco-frontend-core/domain/newbuilding/developer-promo/analytics';
import { ClShellConfigContext, useCustomConfig } from '../ClShellProvider';

const POSSIBLE_ACTIVE_PARTNERS: string[] = [ 'samolet', 'media' ];

const cn = classname.bind(null, 'ClHeader');

const DEFAULT_LOGIN_PATHS: Record<LoginRole, LoginPath> = {
    default: { id: '', url: '/login/' },
    client: { id: '', url: '/login/' },
    partner: { id: '', url: '/login/' },
    professional: { id: '', url: '/login/' },
};

type RawPersonalButtons = NonNullable<NonNullable<ClShellConfigContext>['header']>['personalButtons'];

const toPersonalButtons = (
    raw: RawPersonalButtons | null | undefined
): Record<string, HeaderPersonalButton> | undefined => {
    if (! raw) return undefined;
    const result: Record<string, HeaderPersonalButton> = {};

    if (raw.favorites) {
        result.favorites = {
            url: raw.favorites.url,
            professionalUrl: raw.favorites.professionalUrl ?? undefined,
        };
    }
    if (raw.offerPlacement) {
        result.offerPlacement = {
            url: raw.offerPlacement.url,
            professionalUrl: raw.offerPlacement.professionalUrl ?? undefined,
            authorizedUrl: raw.offerPlacement.authorizedUrl ?? undefined,
        };
    }
    if (raw.savedSearch) {
        result.savedSearch = {
            url: raw.savedSearch.url,
            professionalUrl: raw.savedSearch.professionalUrl ?? undefined,
        };
    }

    return result;
};

export function getRegionForPromo(regionId: number, narrowRegionId?: number) {
    if (
        ! narrowRegionId ||
        Object.values(RegionIdEnum).includes(regionId) ||
        regionId === AdditionalRegionWithMetroIdEnum.EKB ||
        regionId === AdditionalRegionWithMetroIdEnumObl.SVERDLOVSKAYA_OBLAST
    ) {
        return regionId;
    }

    return narrowRegionId;
}

const ClHeader: React.FC<{
    buildLink?: (url: string) => string;
    regionId?: number;
    isFullWidth?: boolean;
    isBordered?: boolean;
}> = ({
    buildLink,
    regionId,
    isFullWidth = true,
    isBordered,
}) => {
    const { auth } = useM2AuthContext();
    const { config } = useShellContext() as unknown as { config: ClShellConfigContext };
    const router = useRouter();

    // Костыль, не работает useShellContext
    const shellConfig = useCustomConfig();

    const effectiveConfig = config ?? shellConfig;

    const rootRegions = useGeoRootRegion();
    const narrowRegionId = regionId && rootRegions.byId(regionId)?.narrowRegion?.id;
    const regionForDeveloperPromo = regionId && getRegionForPromo(regionId, narrowRegionId);

    const developerWithPromoNameByRegion = getDeveloperPromoName(regionForDeveloperPromo);
    const partners = useMemo(
        () => Object.fromEntries(
            Object
                .entries(effectiveConfig?.header?.partners ?? {})
                .filter(([ key, value ]) => POSSIBLE_ACTIVE_PARTNERS.includes(key) && value))
        , [ effectiveConfig?.header?.partners ]);

    const pageType = useMemo(() => {
        const { route } = router.resolve([ SearchRoute, OfferRoute, SearchMapRoute, UnsubscribeRoute ]);

        switch (route.displayName) {
            case 'OfferRoute':
                return 'card';
            case 'SearchRoute':
            case 'SearchMapRoute':
            case 'UnsubscribeRoute':
                return 'serp';
            default:
                return;
        }
    }, [ router ]);

    const onPartnerClick = useCallback((partnerName: string) => {
        const dataObj = pageType && getDeveloperBannerGtmData(partnerName, {
            action: 'click',
            section: 'logo',
            pageType
        });

        dataObj && dataLayerPush(dataObj);
    }, [ pageType ]);

    const currentRole = auth.user?.currentRoleType;

    const items = useMemo(() => {
        const getUrl = (url?: string | null) => buildLink && url?.startsWith('/') ? buildLink(url) : url;

        return effectiveConfig?.header?.items?.map(item => ({
            ...item,

            path: getUrl(item?.path),

            dropdownMenu: item.dropdownMenu?.map(dropDownItem => ({
                ...dropDownItem,
                url: getUrl(dropDownItem?.url),
                id: dropDownItem.id + (dropDownItem.url ?? '0'),
                dropdownMenu: dropDownItem.dropdownMenu?.map(subDropdownItem => ({
                    ...subDropdownItem,
                    url: getUrl(subDropdownItem?.url),
                    id: subDropdownItem.id + (subDropdownItem.url ?? '0')
                }))
            }))
        })) ?? [];
    }, [ effectiveConfig?.header?.items, buildLink ]);

    const personalButtons = useMemo(
        () => toPersonalButtons(effectiveConfig?.header?.personalButtons),
        [ effectiveConfig?.header?.personalButtons ],
    );

    return (
        <div className={cn()}>
            <LayoutComponentHeader
                isColored
                isBordered={isBordered}
                isFullWidth={isFullWidth}
                cdnUrl={effectiveConfig?.cdnUrl ?? 'https://cdn.m2.ru'}
                loginPaths={effectiveConfig?.header?.loginPaths ?? DEFAULT_LOGIN_PATHS}
                items={items}
                partners={partners}
                pathname={router.location.pathname}
                activePartner={developerWithPromoNameByRegion}
                authorizedItems={effectiveConfig?.sidebar}
                roleType={currentRole}
                logoutPath={`/logout?retpath=${encodeURIComponent(router.currentUrl)}`}
                onPartnerClick={onPartnerClick}
                personalButtons={personalButtons}
                hasBurgerMenu
                personalAreaMobileButtonText='Кабинет'
            />
        </div>
    );
};

export default errorCatcherWrap(ClHeader);
